import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
  signal,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { CanComponentDeactivate } from '@app/core/auth/can-component-deactivate.interface';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { LogicTransferFormComponent } from '../components/logic-transfer-form/logic-transfer-form.component';
import {
  LogicTransferFormConfig,
  LOGIC_TRANSFER_HEADER_ACTIONS,
  LOGIC_TRANSFER_TABLE_SECTIONS,
  LOGIC_TRANSFER_FORM_FIELDS,
} from '../logic-transfer.config';
import { LogicTransferService } from '../logic-transfer.service';

@Component({
  selector: 'app-logic-transfer-edit',
  standalone: true,
  imports: [CommonModule, LogicTransferFormComponent, ToastModule, TranslateModule],
  providers: [MessageService, LogicTransferService],
  templateUrl: './logic-transfer-edit.component.html',
  styleUrls: ['./logic-transfer-edit.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LogicTransferEditComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  @ViewChild('formWrapper') private readonly formWrapper?: LogicTransferFormComponent;

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);
  private readonly logicTransferService = inject(LogicTransferService);
  private readonly translateService = inject(TranslateService);
  private readonly destroy$ = new Subject<void>();

  config: LogicTransferFormConfig = {
    title: 'logic_transfer.form.edit.title',
    formFields: [...LOGIC_TRANSFER_FORM_FIELDS],
    headerActions: LOGIC_TRANSFER_HEADER_ACTIONS,
    tableSections: LOGIC_TRANSFER_TABLE_SECTIONS,
    listRoute: '/stock/transfer-order',
  };

  initialData = signal<any>(null);
  loading = signal(false);
  editMode = true;

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.loadTransferOrderData(id);
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Invalid transfer order ID',
      });
      this.router.navigate(['/stock/transfer-order']);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  canDeactivate(): boolean {
    return this.formWrapper?.canDeactivate() ?? true;
  }

  private loadTransferOrderData(id: string): void {
    this.loading.set(true);
    this.logicTransferService.getLogicTransferById(id).subscribe({
      next: (data) => {
        if (data) {
          this.initialData.set(data);
          this.loading.set(false);
        } else {
          this.messageService.add({
            severity: 'warn',
            summary: 'Not Found',
            detail: 'Transfer order not found',
          });
          this.router.navigate(['/stock/transfer-order']);
        }
      },
      error: (error) => {
        // Error loading transfer order
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load transfer order data',
        });
        this.loading.set(false);
        this.router.navigate(['/stock/transfer-order']);
      },
    });
  }
}

